/**
 * Standard API envelope: { success, code, message, data }.
 * Throws on explicit failure; returns inner data on success.
 */
export type ApiEnvelope<T = unknown> = {
  success?: boolean;
  code?: number;
  message?: string;
  data?: T;
};

export class ApiEnvelopeError extends Error {
  readonly code?: number;

  constructor(message: string, code?: number) {
    super(message);
    this.name = 'ApiEnvelopeError';
    this.code = code;
  }
}

export function unwrapEnvelope<T>(raw: unknown, fallback_message = 'API request failed'): T {
  if (raw == null) {
    throw new ApiEnvelopeError(fallback_message);
  }
  if (typeof raw !== 'object') {
    return raw as T;
  }
  const envelope = raw as ApiEnvelope<T>;
  if (envelope.success === false) {
    throw new ApiEnvelopeError(envelope.message ?? fallback_message, envelope.code);
  }
  if ('data' in envelope && envelope.data !== undefined) {
    return envelope.data as T;
  }
  return raw as T;
}

/** Returns inner data when present; otherwise the raw object (for non-envelope payloads). */
export function unwrapEnvelopeOrRaw<T>(raw: unknown): T {
  if (raw != null && typeof raw === 'object' && 'data' in (raw as object)) {
    const envelope = raw as ApiEnvelope<T>;
    if (envelope.data !== undefined) {
      return envelope.data as T;
    }
  }
  return raw as T;
}
